import React, { useEffect, useRef, useState } from 'react';
import { AVATAR_PALETTE } from '../../constants/avatarPalette';
import { useTranslation } from '../../contexts/TranslationContext';
import { apiCall } from '../../utils/api';
import Modal from '../Modal';

interface ExpertPlanProps {
  id: number;
  level: number;
  name?: string;
  priceMonthly?: number;
  featuresDescription?: string;
  featuresList?: string[] | null;
  featured?: boolean | null;
  color?: string | null;
  onUpdate?: () => void;
}

const TierColorPicker: React.FC<{
  value: string | null;
  onChange: (hex: string | null) => void;
  label: string;
}> = ({ value, onChange, label }) => {
  const [open, setOpen] = useState(false);
  const wrapRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const close = (e: MouseEvent) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', close);
    return () => document.removeEventListener('mousedown', close);
  }, [open]);

  return (
    <div className="expert-plan-form__color-picker" ref={wrapRef}>
      <div className="expert-plan-form__color-swatch-wrap">
        <button
          type="button"
          className={`expert-plan-form__color-swatch${!value ? ' expert-plan-form__color-swatch--empty' : ''}`}
          style={value ? { backgroundColor: value } : undefined}
          onClick={() => setOpen(s => !s)}
          aria-label={label}
        />
        {open && (
          <div className="expert-plan-form__color-popover">
            {AVATAR_PALETTE.map(hex => (
              <button
                key={hex}
                type="button"
                className={`expert-plan-form__color-cell${value === hex ? ' expert-plan-form__color-cell--selected' : ''}`}
                style={{ backgroundColor: hex }}
                onClick={() => { onChange(hex); setOpen(false); }}
              />
            ))}
          </div>
        )}
      </div>
      {value && (
        <button type="button" className="expert-plan-form__color-none-btn" onClick={() => onChange(null)}>
          none
        </button>
      )}
    </div>
  );
};

const ExpertPlan: React.FC<ExpertPlanProps> = ({
  id,
  level,
  name,
  priceMonthly,
  featuresDescription,
  featuresList,
  featured,
  color,
  onUpdate
}) => {
  const { t } = useTranslation();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [formData, setFormData] = useState({
    level,
    name: name || '',
    priceMonthly: priceMonthly || '',
    featuresDescription: featuresDescription || '',
    featuresList: featuresList ? [...featuresList] : [] as string[],
    featured: featured || false,
    color: color || null as string | null,
  });

  const isFreeTier = Number(formData.level) === 0;

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleFeatureChange = (index: number, value: string) => {
    setFormData(prev => {
      const next = [...prev.featuresList];
      next[index] = value;
      return { ...prev, featuresList: next };
    });
  };

  const addFeature = () => {
    setFormData(prev => ({ ...prev, featuresList: [...prev.featuresList, ''] }));
  };

  const removeFeature = (index: number) => {
    setFormData(prev => ({
      ...prev,
      featuresList: prev.featuresList.filter((_, i) => i !== index),
    }));
  };

  const isFormValid = () => {
    if (!formData.name.trim()) return false;
    if (!isFreeTier && (!formData.priceMonthly || Number(formData.priceMonthly) <= 0)) return false;
    return true;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!isFormValid()) return;
    setSaving(true);
    try {
      await apiCall(`/tipsters/me/tiers/${id}`, {
        method: 'PATCH',
        body: JSON.stringify({
          level: Number(formData.level),
          name: formData.name,
          price_monthly: isFreeTier ? 0 : Number(formData.priceMonthly),
          features_description: formData.featuresDescription || null,
          features_list: formData.featuresList.filter(f => f.trim()),
          featured: formData.featured,
          color: formData.color || null,
        })
      });
      setIsModalOpen(false);
      onUpdate?.();
    } catch (error) {
      console.error('Failed to update tier:', error);
    } finally {
      setSaving(false);
    }
  };

  const priceDisplay = level === 0
    ? t.tiers.free
    : t.monetization.perMonth.replace('{price}', String(priceMonthly ?? 0));

  return (
    <>
      <div className="expert-plan-card wide__small__text">
        {level === 0 && <span className="expert-plan-card__free-badge wide__small__text">{t.tiers.free}</span>}
        <div className="expert-plan-card__row">
          <span className="expert-plan-card__label">{t.monetization.level}</span>
          <span className="expert-plan-card__value">{level}</span>
        </div>
        <div className="expert-plan-card__row">
          <span className="expert-plan-card__label">{t.monetization.name}</span>
          <span className="expert-plan-card__value">{name || '—'}</span>
        </div>
        <div className="expert-plan-card__row">
          <span className="expert-plan-card__label">{t.monetization.price}</span>
          <span className="expert-plan-card__value">{priceDisplay}</span>
        </div>
        <div className="expert-plan-card__row" style={{ flexDirection: 'column', alignItems: 'flex-start', gap: 'var(--spacing-xs)', borderBottom: 'none', paddingBottom: 0 }}>
          <span className="expert-plan-card__label">{t.monetization.description}</span>
          <span className="expert-plan-card__value">{featuresDescription || '—'}</span>
        </div>
        <button
          className="button_primary"
          onClick={() => setIsModalOpen(true)}
          type="button"
        >
          {t.monetization.editPlan}
        </button>
      </div>

      <Modal isOpen={isModalOpen} onClose={() => setIsModalOpen(false)} title={t.monetization.editPlan}>
        <form className="expert-plan-form" onSubmit={handleSubmit}>
          <div className="expert-plan-form__field">
            <label>{t.monetization.level}</label>
            <input
              type="number"
              name="level"
              className="expert-plan-form__input"
              value={formData.level}
              onChange={handleChange}
              min={0}
            />
          </div>
          <div className="expert-plan-form__field">
            <label>{t.monetization.nameRequired}</label>
            <input
              type="text"
              name="name"
              className="expert-plan-form__input"
              value={formData.name}
              onChange={handleChange}
              placeholder={t.monetization.namePlaceholder}
              required
            />
          </div>
          <div className="expert-plan-form__field">
            <label>{t.monetization.priceMonthly} {!isFreeTier && '*'}</label>
            {isFreeTier ? (
              <div className="expert-plan-form__free-badge">{t.tiers.free}</div>
            ) : (
              <input
                type="number"
                name="priceMonthly"
                className="expert-plan-form__input"
                value={formData.priceMonthly}
                onChange={handleChange}
                min={0.01}
                step={0.01}
                placeholder={t.monetization.pricePlaceholder}
                required
              />
            )}
          </div>
          <div className="expert-plan-form__field">
            <label>{t.monetization.description}</label>
            <textarea
              name="featuresDescription"
              className="expert-plan-form__textarea"
              value={formData.featuresDescription}
              onChange={handleChange}
              placeholder={t.monetization.descriptionPlaceholder}
              rows={3}
            />
          </div>
          <div className="expert-plan-form__field">
            <label>{t.monetization.featuresList}</label>
            <div className="expert-plan-form__features-list">
              {formData.featuresList.map((feat, i) => (
                <div className="expert-plan-form__feature-row" key={i}>
                  <input
                    type="text"
                    className="expert-plan-form__input"
                    value={feat}
                    onChange={e => handleFeatureChange(i, e.target.value)}
                    placeholder={t.monetization.featurePlaceholder}
                  />
                  <button
                    type="button"
                    className="expert-plan-form__feature-remove"
                    onClick={() => removeFeature(i)}
                    aria-label="Remove"
                  >
                    ×
                  </button>
                </div>
              ))}
              <button type="button" className="expert-plan-form__add-feature-btn" onClick={addFeature}>
                + {t.monetization.addFeature}
              </button>
            </div>
          </div>
          <div className="expert-plan-form__field">
            <label>{t.monetization.tierColor}</label>
            <TierColorPicker
              value={formData.color}
              onChange={c => setFormData(prev => ({ ...prev, color: c }))}
              label={t.monetization.tierColor}
            />
          </div>
          <div className="expert-plan-form__field">
            <div className="expert-plan-form__switch-row">
              <label>{t.monetization.featured}</label>
              <label className="expert-plan-form__switch">
                <input
                  type="checkbox"
                  checked={formData.featured}
                  onChange={e => setFormData(prev => ({ ...prev, featured: e.target.checked }))}
                />
                <span className="expert-plan-form__switch-track" />
              </label>
            </div>
          </div>
          <div className="expert-plan-form__actions">
            <button type="button" className="button_secondary" onClick={() => setIsModalOpen(false)} disabled={saving}>
              {t.common.cancel}
            </button>
            <button type="submit" className="button_primary" disabled={saving || !isFormValid()}>
              {saving ? t.expertPanel.saving : t.monetization.saveChanges}
            </button>
          </div>
        </form>
      </Modal>
    </>
  );
};

export default ExpertPlan;
